// src/components/Navbar.jsx
import { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import DropdownPortal from "./DropdownPortal";
import { can, getUserName, podeAcessarSaude } from "../auth/permissions";
import { useTheme } from "../theme/ThemeContext";

export default function Navbar({ usuario, onLogout }) {
  const location = useLocation();

  const producaoBtnRef = useRef(null);
  const pedidosBtnRef = useRef(null);
  const relatoriosBtnRef = useRef(null);
  const financeiroBtnRef = useRef(null);
  const estoqueBtnRef = useRef(null);
  const adminBtnRef = useRef(null);
  const vendasBtnRef = useRef(null);
  const pessoasBtnRef = useRef(null);
  const saudeBtnRef = useRef(null);

  const lastPathRef = useRef(location.pathname);
  const [menuTopoAberto, setMenuTopoAberto] = useState(null);

  // Helper para URL da API em dev/porta 3000
  const getApiUrl = (p) => {
    const cleanPath = p.startsWith('/') ? p : '/' + p;
    if (typeof window === 'undefined') return 'http://localhost:3001' + cleanPath;
    const isDevPort = window.location.port === '3000' || window.location.port === '3002';
    const base = isDevPort
      ? window.location.origin.replace(':' + window.location.port, ':3001')
      : window.location.origin;
    return base + cleanPath;
  };

  // Monitor de Saude (somente para admin)
  const [saudeStatus, setSaudeStatus] = useState(null);
  const [saudeCarregando, setSaudeCarregando] = useState(false);
  const [saudeErro, setSaudeErro] = useState(false);
  const [saudePopoverAberto, setSaudePopoverAberto] = useState(false);

  const temAcessoSaude = useMemo(() => podeAcessarSaude(usuario), [usuario]);

  const consultarSaude = useCallback(async () => {
    if (!temAcessoSaude) return;
    try {
      setSaudeCarregando(true);
      const token =
        localStorage.getItem('authToken') || localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(getApiUrl('/api/sistema/saude'), {
        headers: { Authorization: 'Bearer ' + token },
      });
      if (!res.ok) throw new Error('Status ' + res.status);
      const data = await res.json();
      setSaudeStatus(data);
      setSaudeErro(false);
    } catch (err) {
      setSaudeErro(true);
    } finally {
      setSaudeCarregando(false);
    }
  }, [temAcessoSaude]);

  useEffect(() => {
    if (!temAcessoSaude) return;

    consultarSaude();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        consultarSaude();
      }
    }, 30000);

    const handleVis = () => {
      if (document.visibilityState === 'visible') {
        consultarSaude();
      }
    };

    const handleSync = (e) => {
      if (e?.detail) {
        setSaudeStatus(e.detail);
        setSaudeErro(false);
      }
    };

    document.addEventListener('visibilitychange', handleVis);
    window.addEventListener('km:saude-atualizada', handleSync);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVis);
      window.removeEventListener('km:saude-atualizada', handleSync);
    };
  }, [temAcessoSaude, consultarSaude]);

  const alternarSaudePopover = () => {
    const proximo = !saudePopoverAberto;
    setSaudePopoverAberto(proximo);
    if (proximo) {
      consultarSaude();
    }
  };

  // ✅ novo: menu mobile
  const [mobileOpen, setMobileOpen] = useState(false);

  const nomeUser = useMemo(() => getUserName(usuario), [usuario]);
  const { theme, toggleTheme } = useTheme();
  const topActiveClass =
    theme === "dark"
      ? "bg-blue-500/25 text-blue-100 ring-1 ring-blue-400/45"
      : "bg-slate-200 ";
  const topIdleClass =
    theme === "dark"
      ? "text-slate-200 hover:bg-slate-700/45 hover:text-blue-100"
      : "text-slate-800 hover:bg-slate-100 hover:text-blue-700";

  // ===== Helpers de rota ativa + clique na mesma rota =====
  const isActivePath = (path) => location.pathname === path;

  const isActiveGroup = (paths) =>
    paths.some((p) => location.pathname.startsWith(p));
  
  const handleNavClick = (targetPath, closeMenu = true) => (e) => {
    if (closeMenu) {
      setMenuTopoAberto(null);
      setMobileOpen(false);
    }

    if (location.pathname === targetPath) {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent("page:refresh", { detail: { path: targetPath } }));
    }

  };

  useEffect(() => {
    setMenuTopoAberto(null);
    setMobileOpen(false);
    lastPathRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    const fechar = (e) => {
      if (!e.target.closest(".navbar")) {
        setMenuTopoAberto(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", fechar);
    return () => document.removeEventListener("mousedown", fechar);
  }, []);

  return (
    <nav className="navbar sticky top-0 z-50 bg-gradient-to-r from-slate-50 via-white to-slate-50 border-b border-slate-200 shadow-sm">
      <div className="w-full max-w-none px-3 sm:px-4 py-2.5 flex items-center justify-between gap-2">
        {/* ESQUERDA - MOBILE: botão hambúrguer + Início compacto */}
        <div className="flex items-center gap-2 md:hidden">
          <button
            type="button"
            onClick={() => setMobileOpen((s) => !s)}
            aria-expanded={mobileOpen}
            aria-controls="km-navbar-mobile"
            className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-700 "
            title="Abrir menu"
          >
            {/* ícone hambúrguer */}
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M4 6h16M4 12h16M4 18h16"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>

          <Link
            to="/dashboard"
            onClick={handleNavClick("/dashboard")}
            className={`text-sm font-medium tracking-wide px-3 py-2 rounded-lg transition
              ${isActivePath("/dashboard")
                ? topActiveClass
                : topIdleClass
              }`}
          >
            Início
          </Link>
        </div>

        {/* ESQUERDA - DESKTOP */}
        <div className="hidden md:flex items-center gap-3 text-sm">
          <Link
            to="/dashboard"
            onClick={handleNavClick("/dashboard")}
            className={`font-medium tracking-wide px-3 py-1.5 rounded-full transition
              ${isActivePath("/dashboard")
                ? topActiveClass
                : topIdleClass
              }`}
          >
            Início
          </Link>

          {/* Produção */}
          <div className="relative">
            <button
              ref={producaoBtnRef}
              onClick={() =>
                setMenuTopoAberto(
                  menuTopoAberto === "producao" ? null : "producao"
                )
              }
              className={`font-medium inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition
                ${isActiveGroup([
                "/extrusao",
                "/impressao",
                "/corte",
                "/expedicao",
              ])
                  ? topActiveClass
                  : topIdleClass
                }`}
            >
              Produção <span className="text-xs">▾</span>
            </button>

            <DropdownPortal
              anchorRef={producaoBtnRef}
              open={menuTopoAberto === "producao"}
              onClose={() => setMenuTopoAberto(null)}
            >
              <div className="w-56 mt-2 bg-white rounded-xl shadow-lg border border-slate-100 py-2">
                {can(usuario, "extrusao") && (
                  <Link
                    to="/extrusao"
                    onClick={handleNavClick("/extrusao")}
                    className={`block px-4 py-2.5 text-sm rounded-md
                      ${isActivePath("/extrusao")
                        ? "bg-slate-100 "
                        : "text-slate-700 "
                      }`}
                  >
                    Extrusão
                  </Link>
                )}

                {can(usuario, "impressao") && (
                  <Link
                    to="/impressao"
                    onClick={handleNavClick("/impressao")}
                    className={`block px-4 py-2.5 text-sm rounded-md
                      ${isActivePath("/impressao")
                        ? "bg-slate-100 "
                        : "text-slate-700 "
                      }`}
                  >
                    Impressão
                  </Link>
                )}

                {can(usuario, "corte") && (
                  <Link
                    to="/corte"
                    onClick={handleNavClick("/corte")}
                    className={`block px-4 py-2.5 text-sm rounded-md
                      ${isActivePath("/corte")
                        ? "bg-slate-100 "
                        : "text-slate-700 "
                      }`}
                  >
                    Corte
                  </Link>
                )}

                {can(usuario, "expedicao") && (
                  <Link
                    to="/expedicao"
                    onClick={handleNavClick("/expedicao")}
                    className={`block px-4 py-2.5 text-sm rounded-md
                      ${isActivePath("/expedicao")
                        ? "bg-slate-100 "
                        : "text-slate-700 "
                      }`}
                  >
                    Expedição
                  </Link>
                )}
              </div>
            </DropdownPortal>
          </div>

          {/* Relatórios */}
          {can(usuario, "relatorios") && (
            <div className="relative">
              <button
                ref={relatoriosBtnRef}
                onClick={() =>
                  setMenuTopoAberto(
                    menuTopoAberto === "relatorios" ? null : "relatorios"
                  )
                }
                className={`font-medium inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition
                  ${isActiveGroup(["/relatorios"])
                    ? topActiveClass
                    : topIdleClass
                  }`}
              >
                Relatórios <span className="text-xs">▾</span>
              </button>

              <DropdownPortal
                anchorRef={relatoriosBtnRef}
                open={menuTopoAberto === "relatorios"}
                onClose={() => setMenuTopoAberto(null)}
              >
                <div className="w-64 mt-2 bg-white rounded-xl shadow-lg border border-slate-100 py-2">
                  <Link
                    to={{ pathname: "/relatorios", search: "?aba=geral" }}
                    onClick={handleNavClick("/relatorios")}
                    className={`block px-4 py-2.5 text-sm rounded-md
                      ${isActivePath("/relatorios")
                        ? "bg-slate-100 "
                        : "text-slate-700 "
                      }`}
                  >
                    Geral
                  </Link>
                  <Link
                    to={{ pathname: "/relatorios", search: "?aba=extrusao" }}
                    onClick={handleNavClick("/relatorios")}
                    className={`block px-4 py-2.5 text-sm rounded-md
                      ${isActivePath("/relatorios")
                        ? "bg-slate-100 "
                        : "text-slate-700 "
                      }`}
                  >
                    Extrusão
                  </Link>
                  <Link
                    to={{ pathname: "/relatorios", search: "?aba=impressao" }}
                    onClick={handleNavClick("/relatorios")}
                    className={`block px-4 py-2.5 text-sm rounded-md
                      ${isActivePath("/relatorios")
                        ? "bg-slate-100 "
                        : "text-slate-700 "
                      }`}
                  >
                    Impressão
                  </Link>
                  <Link
                    to={{ pathname: "/relatorios", search: "?aba=corte" }}
                    onClick={handleNavClick("/relatorios")}
                    className={`block px-4 py-2.5 text-sm rounded-md
                      ${isActivePath("/relatorios")
                        ? "bg-slate-100 "
                        : "text-slate-700 "
                      }`}
                  >
                    Corte
                  </Link>
                </div>
              </DropdownPortal>
            </div>
          )}

          {/* Financeiro */}
          {can(usuario, "financeiro") && (
            <div className="relative">
              <button
                ref={financeiroBtnRef}
                onClick={() =>
                  setMenuTopoAberto(
                    menuTopoAberto === "financeiro" ? null : "financeiro"
                  )
                }
                className={`font-medium inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition
                  ${isActiveGroup([
                  "/financeiro",
                  "/financeiro/contas-pagar",
                  "/contas-pagar",
                  "/orcamento-comissao",
                  "/contas-receber",
                  "/relatorio-financeiro",
                ])
                    ? topActiveClass
                    : topIdleClass
                  }`}
              >
                Financeiro <span className="text-xs">▾</span>
              </button>

              <DropdownPortal
                anchorRef={financeiroBtnRef}
                open={menuTopoAberto === "financeiro"}
                onClose={() => setMenuTopoAberto(null)}
              >
                <div className="w-64 mt-2 bg-white rounded-xl shadow-lg border border-slate-100 py-2">
                  <Link
                    to="/financeiro"
                    onClick={handleNavClick("/financeiro")}
                    className={`block px-4 py-2.5 text-sm rounded-md font-semibold
                      ${isActivePath("/financeiro") && !isActivePath("/financeiro/contas-pagar")
                        ? "bg-slate-100 "
                        : "text-slate-700 "
                      }`}
                  >
                    Painel Financeiro
                  </Link>

                  <Link
                    to="/financeiro/contas-pagar"
                    onClick={handleNavClick("/financeiro/contas-pagar")}
                    className={`block px-4 py-2.5 text-sm rounded-md
                      ${isActivePath("/financeiro/contas-pagar") || isActivePath("/contas-pagar")
                        ? "bg-slate-100 "
                        : "text-slate-700 "
                      }`}
                  >
                    Contas à Pagar
                  </Link>

                  <Link
                    to="/orcamento-comissao"
                    onClick={handleNavClick("/orcamento-comissao")}
                    className={`block px-4 py-2.5 text-sm rounded-md
                      ${isActivePath("/orcamento-comissao")
                        ? "bg-slate-100 "
                        : "text-slate-700 "
                      }`}
                  >
                    Comissões
                  </Link>

                  <Link
                    to="/contas-receber"
                    onClick={handleNavClick("/contas-receber")}
                    className={`block px-4 py-2.5 text-sm rounded-md
                      ${isActivePath("/contas-receber")
                        ? "bg-slate-100 "
                        : "text-slate-700 "
                      }`}
                  >
                    Contas à Receber
                  </Link>

                  {can(usuario, "relatorioFinanceiro") && (
                    <Link
                      to="/relatorio-financeiro"
                      onClick={handleNavClick("/relatorio-financeiro")}
                      className={`block px-4 py-2.5 text-sm rounded-md
                        ${isActivePath("/relatorio-financeiro")
                          ? "bg-slate-100 "
                          : "text-slate-700 "
                        }`}
                    >
                      Relatórios
                    </Link>
                  )}
                </div>
              </DropdownPortal>
            </div>
          )}

          {/* Estoque */}
          <div className="relative">
            <button
              ref={estoqueBtnRef}
              onClick={() =>
                setMenuTopoAberto(
                  menuTopoAberto === "estoque" ? null : "estoque"
                )
              }
              className={`font-medium inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition
                ${isActiveGroup([
                "/estoque",
                "/controle-estoque",
                "/produtos-venda",
                "/cadastro-fornecedores",
                "/suprimentos",
              ])
                  ? topActiveClass
                  : topIdleClass
                }`}
            >
              Estoque <span className="text-xs">▾</span>
            </button>

            <DropdownPortal
              anchorRef={estoqueBtnRef}
              open={menuTopoAberto === "estoque"}
              onClose={() => setMenuTopoAberto(null)}
            >
              <div className="w-64 mt-2 bg-white rounded-xl shadow-lg border border-slate-100 py-2">
                <Link
                  to="/estoque"
                  onClick={handleNavClick("/estoque")}
                  className={`block px-4 py-2.5 text-sm rounded-md
                    ${isActivePath("/estoque")
                      ? "bg-slate-100 "
                      : "text-slate-700 "
                    }`}
                >
                  Pedidos de Compra
                </Link>
                <Link
                  to="/controle-estoque"
                  onClick={handleNavClick("/controle-estoque")}
                  className={`block px-4 py-2.5 text-sm rounded-md
                    ${isActivePath("/controle-estoque")
                      ? "bg-slate-100 "
                      : "text-slate-700 "
                    }`}
                >
                  Controle de Estoque
                </Link>
                <Link
                  to="/produtos-venda"
                  onClick={handleNavClick("/produtos-venda")}
                  className={`block px-4 py-2.5 text-sm rounded-md
                    ${isActivePath("/produtos-venda")
                      ? "bg-slate-100 "
                      : "text-slate-700 "
                    }`}
                >
                  Produtos de Venda
                </Link>
                <Link
                  to="/cadastro-fornecedores"
                  onClick={handleNavClick("/cadastro-fornecedores")}
                  className={`block px-4 py-2.5 text-sm rounded-md
                    ${isActivePath("/cadastro-fornecedores")
                      ? "bg-slate-100 "
                      : "text-slate-700 "
                    }`}
                >
                  Fornecedores
                </Link>
                <Link to="/suprimentos"
                onClick={handleNavClick("/suprimentos")}
                className={`block px-4 py-2.5 text-sm rounded-md
                    ${isActivePath("/suprimentos")
                      ? "bg-slate-100 "
                      : "text-slate-700 "
                    }`}
                >
                  Suprimentos
                </Link>
              </div>
            </DropdownPortal>
          </div>

          {/* Pedidos (desativado temporariamente) */}
          {false && can(usuario, "pedidos") && (
            <div className="relative">
              <button
                ref={pedidosBtnRef}
                onClick={() =>
                  setMenuTopoAberto(
                    menuTopoAberto === "pedidos" ? null : "pedidos"
                  )
                }
                className="font-medium text-slate-800 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full hover:bg-slate-100 hover:text-blue-700 transition"
              >
                Pedidos <span className="text-xs">▾</span>
              </button>
              <DropdownPortal
                anchorRef={pedidosBtnRef}
                open={menuTopoAberto === "pedidos"}
                onClose={() => setMenuTopoAberto(null)}
              >
                <div className="w-64 mt-2 bg-white rounded-xl shadow-lg border border-slate-100 py-2">
                  <Link
                    to={{ pathname: "/pedidos", search: "?aba=clientes" }}
                    className="block px-4 py-2.5 text-sm text-slate-700  rounded-md"
                  >
                    Clientes
                  </Link>
                  <Link
                    to={{ pathname: "/pedidos", search: "?aba=pedidos" }}
                    className="block px-4 py-2.5 text-sm text-slate-700  rounded-md"
                  >
                    Pedidos
                  </Link>
                  <Link
                    to={{ pathname: "/pedidos", search: "?aba=relatorios" }}
                    className="block px-4 py-2.5 text-sm text-slate-700  rounded-md"
                  >
                    Relatórios
                  </Link>
                </div>
              </DropdownPortal>
            </div>
          )}

          {/* Vendas */}
          {(can(usuario, "vendas") || can(usuario, "notasfiscais")) && (
            <div className="relative">
              <button
                ref={vendasBtnRef}
                onClick={() =>
                  setMenuTopoAberto(
                    menuTopoAberto === "vendas" ? null : "vendas"
                  )
                }
                className={`font-medium inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition
                  ${isActiveGroup([
                  "/orcamentos",
                  "/vendas",
                  "/relatorio-vendas",
                  "/notasfiscais",
                ])
                    ? topActiveClass
                    : topIdleClass
                  }`}
              >
                Vendas <span className="text-xs">▾</span>
              </button>

              <DropdownPortal
                anchorRef={vendasBtnRef}
                open={menuTopoAberto === "vendas"}
                onClose={() => setMenuTopoAberto(null)}
              >
                <div className="w-64 mt-2 bg-white rounded-xl shadow-lg border border-slate-100 py-2">
                  <Link
                    to="/orcamentos"
                    onClick={handleNavClick("/orcamentos")}
                    className={`block px-4 py-2.5 text-sm rounded-md
                      ${isActivePath("/orcamentos")
                        ? "bg-slate-100"
                        : "text-slate-700 "
                      }`}
                  >
                    Orçamento de Vendas
                  </Link>
                  <Link
                    to="/vendas"
                    onClick={handleNavClick("/vendas")}
                    className={`block px-4 py-2.5 text-sm rounded-md
                      ${isActivePath("/vendas")
                        ? "bg-slate-100"
                        : "text-slate-700 "
                      }`}
                  >
                    Pedidos de Vendas
                  </Link>
                  <Link
                    to="/relatorio-vendas"
                    onClick={handleNavClick("/relatorio-vendas")}
                    className={`block px-4 py-2.5 text-sm rounded-md
                      ${isActivePath("/relatorio-vendas")
                        ? "bg-slate-100 "
                        : "text-slate-700 "
                      }`}
                  >
                    Relatório de Vendas
                  </Link>                  {can(usuario, "notasfiscais") && (
                    <>
                      <Link
                        to="/notasfiscais"
                        onClick={handleNavClick("/notasfiscais")}
                        className={`block px-4 py-2.5 text-sm rounded-md
                          ${isActivePath("/notasfiscais")
                            ? "bg-slate-100 "
                            : "text-slate-700 "
                          }`}
                      >
                        Notas Fiscais
                      </Link>
                    </>
                  )}                </div>
              </DropdownPortal>
            </div>
          )}

          {/* Gestão de Pessoas (RH/DP/Ponto) */}
          {(can(usuario, "rh") || can(usuario, "dp") || can(usuario, "ponto")) && (
            <div className="relative">
              <button
                ref={pessoasBtnRef}
                onClick={() =>
                  setMenuTopoAberto(
                    menuTopoAberto === "pessoas" ? null : "pessoas"
                  )
                }
                className={`font-medium inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition
                  ${isActiveGroup([
                  "/recursos-humanos",
                  "/departamento-pessoal",
                  "/controle-ponto",
                  "/rh",
                  "/dp",
                ])
                    ? topActiveClass
                    : topIdleClass
                  }`}
              >
                Gestão de Pessoas <span className="text-xs">▾</span>
              </button>

              <DropdownPortal
                anchorRef={pessoasBtnRef}
                open={menuTopoAberto === "pessoas"}
                onClose={() => setMenuTopoAberto(null)}
              >
                <div className="w-64 mt-2 bg-white rounded-xl shadow-lg border border-slate-100 py-2">
                  {can(usuario, "rh") && (
                    <Link
                      to="/recursos-humanos"
                      onClick={handleNavClick("/recursos-humanos")}
                      className={`block px-4 py-2.5 text-sm rounded-md
                        ${isActivePath("/recursos-humanos")
                          ? "bg-slate-100 "
                          : "text-slate-700 "
                        }`}
                    >
                      Recursos Humanos
                    </Link>
                  )}

                  {can(usuario, "dp") && (
                    <Link
                      to="/departamento-pessoal"
                      onClick={handleNavClick("/departamento-pessoal")}
                      className={`block px-4 py-2.5 text-sm rounded-md
                        ${isActivePath("/departamento-pessoal")
                          ? "bg-slate-100 "
                          : "text-slate-700 "
                        }`}
                    >
                      Departamento Pessoal
                    </Link>
                  )}

                  {can(usuario, "ponto") && (
                    <Link
                      to="/controle-ponto"
                      onClick={handleNavClick("/controle-ponto")}
                      className={`block px-4 py-2.5 text-sm rounded-md
                        ${isActivePath("/controle-ponto")
                          ? "bg-slate-100 "
                          : "text-slate-700 "
                        }`}
                    >
                      Controle de Ponto
                    </Link>
                  )}
                </div>
              </DropdownPortal>
            </div>
          )}

          {/* Administração */}
          {(can(usuario, "cadastroUsuario") || can(usuario, "custos") || can(usuario, "planilhasAuxiliares")) && (
            <div className="relative">
              <button
                ref={adminBtnRef}
                onClick={() =>
                  setMenuTopoAberto(
                    menuTopoAberto === "admin" ? null : "admin"
                  )
                }
                className={`font-medium inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition
                  ${isActiveGroup([
                  "/cadastro-usuario",
                  "/vendedores",
                  "/configuracao-empresa",
                  "/custos",
                  "/planilhas-auxiliares",
                ])
                    ? topActiveClass
                    : topIdleClass
                  }`}
              >
                Administração <span className="text-xs">▾</span>
              </button>

              <DropdownPortal
                anchorRef={adminBtnRef}
                open={menuTopoAberto === "admin"}
                onClose={() => setMenuTopoAberto(null)}
              >
                <div className="w-64 mt-2 bg-white rounded-xl shadow-lg border border-slate-100 py-2">
                  <Link
                    to="/cadastro-usuario"
                    onClick={handleNavClick("/cadastro-usuario")}
                    className={`block px-4 py-2.5 text-sm rounded-md
                      ${isActivePath("/cadastro-usuario")
                        ? "bg-slate-100 "
                        : "text-slate-700 "
                      }`}
                  >
                    Cadastrar Usuário
                  </Link>

                  <Link
                    to="/vendedores"
                    onClick={handleNavClick("/vendedores")}
                    className={`block px-4 py-2.5 text-sm rounded-md
                      ${isActivePath("/vendedores")
                        ? "bg-slate-100 "
                        : "text-slate-700 "
                      }`}
                  >
                    Vendedores
                  </Link>
                  {(can(usuario, "cadastroUsuario") || can(usuario, "custos")) && (
                    <Link
                      to="/custos"
                      onClick={handleNavClick("/custos")}
                      className={`block px-4 py-2.5 text-sm rounded-md
                      ${isActivePath("/custos")
                          ? "bg-slate-100 "
                          : "text-slate-700 "
                        }`}
                    >
                      Custos
                    </Link>
                  )}

                  <Link
                    to="/configuracao-empresa"
                    onClick={handleNavClick("/configuracao-empresa")}
                    className={`block px-4 py-2.5 text-sm rounded-md
                      ${isActivePath("/configuracao-empresa")
                        ? "bg-slate-100 "
                        : "text-slate-700 "
                      }`}
                  >
                    Configuração da Empresa
                  </Link>

                  {can(usuario, "planilhasAuxiliares") && (
                    <Link
                      to="/planilhas-auxiliares"
                      onClick={handleNavClick("/planilhas-auxiliares")}
                      className={`block px-4 py-2.5 text-sm rounded-md
                        ${isActivePath("/planilhas-auxiliares")
                          ? "bg-slate-100 "
                          : "text-slate-700 "
                        }`}
                    >
                      Planilhas Auxiliares
                    </Link>
                  )}
                  {temAcessoSaude && (
                    <Link
                      to="/saude-sistema"
                      onClick={handleNavClick("/saude-sistema")}
                      className={`block px-4 py-2.5 text-sm rounded-md
                        ${isActivePath("/saude-sistema")
                          ? "bg-slate-100 "
                          : "text-slate-700 "
                        }`}
                    >
                      Saúde do Sistema
                    </Link>
                  )}
                </div>
              </DropdownPortal>
            </div>
          )}
        </div>

        {/* DIREITA (desktop + mobile) */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Monitor de Saúde (somente para quem tem acesso) */}
          {temAcessoSaude && (
            <div className="relative">
              <button
                ref={saudeBtnRef}
                type="button"
                onClick={alternarSaudePopover}
                title={
                  saudeErro
                    ? "Servidor sem resposta"
                    : saudeStatus?.nivel === "critico"
                    ? "Servidor em estado CRÍTICO"
                    : saudeStatus?.nivel === "atencao"
                    ? "Servidor em estado de ATENÇÃO"
                    : "Servidor Saudável"
                }
                className={`relative inline-flex items-center justify-center p-1.5 rounded-full border transition active:scale-95 ${
                  saudeErro
                    ? "bg-rose-500/20 text-rose-500 border-rose-500/50"
                    : saudeStatus?.nivel === "critico"
                    ? "bg-rose-500/20 text-rose-400 border-rose-500/40 animate-pulse"
                    : saudeStatus?.nivel === "atencao"
                    ? "bg-amber-500/20 text-amber-400 border-amber-500/40"
                    : "bg-emerald-500/10 text-emerald-500 border-emerald-500/30 hover:bg-emerald-500/20"
                }`}
              >
                {/* Ícone Cardíaco / Pulso de Saúde */}
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>

                {/* Badge com quantidade ou erro */}
                {saudeErro ? (
                  <span className="hidden sm:inline-block ml-1 text-[10px] font-bold text-rose-500">
                    Sem resposta
                  </span>
                ) : saudeStatus?.alertasAtivos && saudeStatus.alertasAtivos.length > 0 ? (
                  <span
                    data-testid="saude-badge-count"
                    className={`absolute -top-1 -right-1 px-1 min-w-[16px] h-4 flex items-center justify-center text-[10px] font-black rounded-full text-white shadow-sm ${
                      saudeStatus.nivel === "critico" ? "bg-rose-600" : "bg-amber-500"
                    }`}
                  >
                    {saudeStatus.alertasAtivos.length}
                  </span>
                ) : saudeStatus?.nivel === "critico" ? (
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-rose-600 ring-2 ring-slate-900 animate-ping" />
                ) : null}
              </button>

              <DropdownPortal
                anchorRef={saudeBtnRef}
                open={saudePopoverAberto}
                onClose={() => setSaudePopoverAberto(false)}
                align="right"
                className="bg-transparent border-0 shadow-none p-0"
              >
                <div
                  data-testid="saude-popover-container"
                  className="w-80 sm:w-96 max-w-[calc(100vw-16px)] mt-2 bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl p-4 text-slate-200 box-border overflow-hidden"
                >
                  <div className="flex items-center justify-between border-b border-slate-800 pb-2 mb-3">
                    <span className="font-bold text-sm text-white flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${
                        saudeErro
                          ? "bg-rose-500"
                          : saudeStatus?.nivel === "critico"
                          ? "bg-rose-500 animate-pulse"
                          : saudeStatus?.nivel === "atencao"
                          ? "bg-amber-400"
                          : "bg-emerald-400"
                      }`}></span>
                      Saúde do Servidor
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                        saudeErro
                          ? "bg-rose-500/20 text-rose-300 border border-rose-500/40"
                          : saudeStatus?.nivel === "critico"
                          ? "bg-rose-500/20 text-rose-300 border border-rose-500/40"
                          : saudeStatus?.nivel === "atencao"
                          ? "bg-amber-500/20 text-amber-300 border border-amber-500/40"
                          : "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                      }`}>
                        {saudeErro
                          ? "ERRO"
                          : saudeStatus?.nivel === "critico"
                          ? "CRÍTICO"
                          : saudeStatus?.nivel === "atencao"
                          ? "ATENÇÃO"
                          : "OPERACIONAL"}
                      </span>
                    </span>
                    <span className="text-[11px] font-mono text-slate-400 flex items-center gap-1.5">
                      Lag: <b className={`font-mono ${
                        (saudeStatus?.lagAtualMs || 0) > 1000 ? "text-amber-400 font-bold" : "text-slate-200"
                      }`}>{saudeStatus?.lagAtualMs ?? "--"} ms</b>
                      {saudeCarregando && (
                        <svg className="w-3 h-3 animate-spin text-slate-400" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                        </svg>
                      )}
                    </span>
                  </div>

                  {saudeErro ? (
                    <div className="p-3 bg-rose-950/40 border border-rose-800/60 rounded-xl text-rose-300 text-xs">
                      ⚠️ O servidor não respondeu à consulta de saúde.
                    </div>
                  ) : saudeStatus?.alertasAtivos && saudeStatus.alertasAtivos.length > 0 ? (
                    <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
                      <div className="text-xs font-semibold text-amber-300">
                        Alertas nos últimos 10 min ({saudeStatus.alertasAtivos.length}):
                      </div>
                      {saudeStatus.alertasAtivos.map((alerta, i) => (
                        <div
                          key={alerta.id || i}
                          className="p-2 bg-slate-950/80 rounded-lg border border-slate-800 text-xs flex flex-col gap-0.5"
                        >
                          <div className="flex items-center justify-between text-slate-200 font-mono text-[11px] gap-2">
                            <span className="truncate max-w-[180px] sm:max-w-[240px]" title={alerta.rota}>
                              {alerta.metodo} {alerta.rota}
                            </span>
                            <span className="font-bold text-amber-400 shrink-0">
                              {alerta.duracaoMs} ms
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-[10px] text-slate-400">
                            <span>👤 {alerta.usuario || "sistema"}</span>
                            <span>{alerta.criadoEm ? alerta.criadoEm.split(" ")[1] : ""}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-4 text-center text-xs text-slate-400">
                      {saudeStatus?.nivel === "critico"
                        ? "⚠️ Servidor com alerta crítico. Verifique backup ou engasgo."
                        : "✅ Nenhum alerta nos últimos 10 minutos. Servidor com ótimo desempenho."}
                    </div>
                  )}

                  <div className="pt-2 border-t border-slate-800 flex justify-end">
                    <Link
                      to="/saude-sistema"
                      onClick={() => setSaudePopoverAberto(false)}
                      className="text-xs font-semibold text-indigo-400 hover:text-indigo-300 underline"
                    >
                      Ver saúde do sistema →
                    </Link>
                  </div>
                </div>
              </DropdownPortal>
            </div>
          )}

          <button
            type="button"
            onClick={toggleTheme}
            aria-label="Alternar tema"
            aria-pressed={theme === "dark"}
            className={`relative inline-flex h-6 w-11 items-center rounded-full border transition focus:outline-none focus:ring-2 focus:ring-blue-500/40
              ${theme === "dark" ? "bg-slate-700 border-slate-600" : "bg-slate-200 border-slate-300"}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition
                ${theme === "dark" ? "translate-x-5" : "translate-x-1"}`}
            />
          </button>
          <span className="text-xs font-medium tracking-wide text-slate-600 bg-slate-100 px-2.5 sm:px-3 py-1.5 rounded-full max-w-[140px] sm:max-w-[220px] truncate">
            👤 {nomeUser}
          </span>
          <button
            onClick={onLogout}
            className="text-xs sm:text-sm font-medium text-red-600 hover:text-red-700 border border-red-500 hover:bg-red-50 px-3 sm:px-3.5 py-1.5 rounded-full transition"
          >
            Sair
          </button>
        </div>
      </div>

      {/* ======================== */}
      {/* MENU MOBILE VERTICAL     */}
      {/* ======================== */}
      {mobileOpen && (
        <div
          id="km-navbar-mobile"
          className="md:hidden border-t border-slate-200 bg-white/95 backdrop-blur"
        >
          <div className="px-3 py-3 space-y-2">
            {/* Produção */}
            {(can(usuario, "extrusao") ||
              can(usuario, "impressao") ||
              can(usuario, "corte") ||
              can(usuario, "expedicao")) && (
                <details className="rounded-lg border border-slate-100 bg-white">
                  <summary className="cursor-pointer list-none px-3 py-2 text-sm font-medium text-slate-800 flex items-center justify-between">
                    <span>Produção</span>
                    <span className="text-xs text-slate-400">▾</span>
                  </summary>
                  <div className="px-2 pb-2">
                    {can(usuario, "extrusao") && (
                      <Link
                        to="/extrusao"
                        onClick={handleNavClick("/extrusao")}
                        className="block px-3 py-2 rounded-md text-sm text-slate-700 "
                      >
                        Extrusão
                      </Link>
                    )}
                    {can(usuario, "impressao") && (
                      <Link
                        to="/impressao"
                        onClick={handleNavClick("/impressao")}
                        className="block px-3 py-2 rounded-md text-sm text-slate-700 "
                      >
                        Impressão
                      </Link>
                    )}
                    {can(usuario, "corte") && (
                      <Link
                        to="/corte"
                        onClick={handleNavClick("/corte")}
                        className="block px-3 py-2 rounded-md text-sm text-slate-700 "
                      >
                        Corte
                      </Link>
                    )}
                    {can(usuario, "expedicao") && (
                      <Link
                        to="/expedicao"
                        onClick={handleNavClick("/expedicao")}
                        className="block px-3 py-2 rounded-md text-sm text-slate-700 "
                      >
                        Expedição
                      </Link>
                    )}
                  </div>
                </details>
              )}

            {/* Relatórios */}
            {can(usuario, "relatorios") && (
              <details className="rounded-lg border border-slate-100 bg-white">
                <summary className="cursor-pointer list-none px-3 py-2 text-sm font-medium text-slate-800 flex items-center justify-between">
                  <span>Relatórios</span>
                  <span className="text-xs text-slate-400">▾</span>
                </summary>
                <div className="px-2 pb-2">
                  <Link
                    to={{ pathname: "/relatorios", search: "?aba=geral" }}
                    onClick={handleNavClick("/relatorios")}
                    className="block px-3 py-2 rounded-md text-sm text-slate-700 "
                  >
                    Geral
                  </Link>
                  <Link
                    to={{ pathname: "/relatorios", search: "?aba=extrusao" }}
                    onClick={handleNavClick("/relatorios")}
                    className="block px-3 py-2 rounded-md text-sm text-slate-700 "
                  >
                    Extrusão
                  </Link>
                  <Link
                    to={{ pathname: "/relatorios", search: "?aba=impressao" }}
                    onClick={handleNavClick("/relatorios")}
                    className="block px-3 py-2 rounded-md text-sm text-slate-700 "
                  >
                    Impressão
                  </Link>
                  <Link
                    to={{ pathname: "/relatorios", search: "?aba=corte" }}
                    onClick={handleNavClick("/relatorios")}
                    className="block px-3 py-2 rounded-md text-sm text-slate-700 "
                  >
                    Corte
                  </Link>
                </div>
              </details>
            )}

            {/* Financeiro */}
            {can(usuario, "financeiro") && (
              <details className="rounded-lg border border-slate-100 bg-white">
                <summary className="cursor-pointer list-none px-3 py-2 text-sm font-medium text-slate-800 flex items-center justify-between">
                  <span>Financeiro</span>
                  <span className="text-xs text-slate-400">▾</span>
                </summary>
                <div className="px-2 pb-2">
                  <Link
                    to="/financeiro"
                    onClick={handleNavClick("/financeiro")}
                    className="block px-3 py-2 rounded-md text-sm font-semibold text-slate-800 "
                  >
                    Painel Financeiro
                  </Link>
                  <Link
                    to="/financeiro/contas-pagar"
                    onClick={handleNavClick("/financeiro/contas-pagar")}
                    className="block px-3 py-2 rounded-md text-sm text-slate-700 "
                  >
                    Contas à Pagar
                  </Link>
                  <Link
                    to="/orcamento-comissao"
                    onClick={handleNavClick("/orcamento-comissao")}
                    className="block px-3 py-2 rounded-md text-sm text-slate-700 "
                  >
                    Comissões
                  </Link>
                  <Link
                    to="/contas-receber"
                    onClick={handleNavClick("/contas-receber")}
                    className="block px-3 py-2 rounded-md text-sm text-slate-700 "
                  >
                    Contas à Receber
                  </Link>
                  {can(usuario, "relatorioFinanceiro") && (
                    <Link
                      to="/relatorio-financeiro"
                      onClick={handleNavClick("/relatorio-financeiro")}
                      className="block px-3 py-2 rounded-md text-sm text-slate-700 "
                    >
                      Relatórios
                    </Link>
                  )}
                </div>
              </details>
            )}

            {/* Estoque */}
            <details className="rounded-lg border border-slate-100 bg-white">
              <summary className="cursor-pointer list-none px-3 py-2 text-sm font-medium text-slate-800 flex items-center justify-between">
                <span>Estoque</span>
                <span className="text-xs text-slate-400">▾</span>
              </summary>
              <div className="px-2 pb-2">
                <Link
                  to="/estoque"
                  onClick={handleNavClick("/estoque")}
                  className="block px-3 py-2 rounded-md text-sm text-slate-700 "
                >
                  Pedidos de Compra
                </Link>
                <Link
                  to="/controle-estoque"
                  onClick={handleNavClick("/controle-estoque")}
                  className="block px-3 py-2 rounded-md text-sm text-slate-700 "
                >
                  Controle de Estoque
                </Link>
                <Link
                  to="/produtos-venda"
                  onClick={handleNavClick("/produtos-venda")}
                  className="block px-3 py-2 rounded-md text-sm text-slate-700 "
                >
                  Produtos de Venda
                </Link>
                <Link
                  to="/cadastro-fornecedores"
                  onClick={handleNavClick("/cadastro-fornecedores")}
                  className="block px-3 py-2 rounded-md text-sm text-slate-700 "
                >
                  Fornecedores
                </Link>
                <Link to="/suprimentos"
                onClick={handleNavClick("/suprimentos")}
                className="block px-3 py-2 rounded-md text-sm text-slate-700"
                >
                  Suprimentos
                </Link>
              </div>
            </details>

            {/* Vendas */}
            {(can(usuario, "vendas") || can(usuario, "notasfiscais")) && (
              <details className="rounded-lg border border-slate-100 bg-white">
                <summary className="cursor-pointer list-none px-3 py-2 text-sm font-medium text-slate-800 flex items-center justify-between">
                  <span>Vendas</span>
                  <span className="text-xs text-slate-400">▾</span>
                </summary>
                <div className="px-2 pb-2">
                  <Link
                    to="/orcamentos"
                    onClick={handleNavClick("/orcamentos")}
                    className="block px-3 py-2 rounded-md text-sm text-slate-800 "
                  >
                    Orçamento de Vendas
                  </Link>
                  <Link
                    to="/vendas"
                    onClick={handleNavClick("/vendas")}
                    className="block px-3 py-2 rounded-md text-sm text-slate-700 "
                  >
                    Pedidos de Vendas
                  </Link>
                  <Link
                    to="/relatorio-vendas"
                    onClick={handleNavClick("/relatorio-vendas")}
                    className="block px-3 py-2 rounded-md text-sm text-slate-700 "
                  >
                    Relatório de Vendas
                  </Link>                  {can(usuario, "notasfiscais") && (
                    <>
                      <Link
                        to="/notasfiscais"
                        onClick={handleNavClick("/notasfiscais")}
                        className={`block px-4 py-2.5 text-sm rounded-md
                          ${isActivePath("/notasfiscais")
                            ? "bg-slate-100 "
                            : "text-slate-700 "
                          }`}
                      >
                        Notas Fiscais
                      </Link>
                    </>
                  )}                </div>
              </details>
            )}

            {/* Gestão de Pessoas (RH/DP/Ponto) */}
            {(can(usuario, "rh") || can(usuario, "dp") || can(usuario, "ponto")) && (
              <details className="rounded-lg border border-slate-100 bg-white">
                <summary className="cursor-pointer list-none px-3 py-2 text-sm font-medium text-slate-800 flex items-center justify-between">
                  <span>Gestão de Pessoas</span>
                  <span className="text-xs text-slate-400">▾</span>
                </summary>
                <div className="px-2 pb-2">
                  {can(usuario, "rh") && (
                    <Link
                      to="/recursos-humanos"
                      onClick={handleNavClick("/recursos-humanos")}
                      className="block px-3 py-2 rounded-md text-sm text-slate-700 "
                    >
                      Recursos Humanos
                    </Link>
                  )}

                  {can(usuario, "dp") && (
                    <Link
                      to="/departamento-pessoal"
                      onClick={handleNavClick("/departamento-pessoal")}
                      className="block px-3 py-2 rounded-md text-sm text-slate-700 "
                    >
                      Departamento Pessoal
                    </Link>
                  )}

                  {can(usuario, "ponto") && (
                    <Link
                      to="/controle-ponto"
                      onClick={handleNavClick("/controle-ponto")}
                      className="block px-3 py-2 rounded-md text-sm text-slate-700 "
                    >
                      Controle de Ponto
                    </Link>
                  )}
                </div>
              </details>
            )}

            {/* Administração */}
            {(can(usuario, "cadastroUsuario") || can(usuario, "custos") || can(usuario, "planilhasAuxiliares")) && (
              <details className="rounded-lg border border-slate-100 bg-white">
                <summary className="cursor-pointer list-none px-3 py-2 text-sm font-medium text-slate-800 flex items-center justify-between">
                  <span>Administração</span>
                  <span className="text-xs text-slate-400">▾</span>
                </summary>
                <div className="px-2 pb-2">
                  <Link
                    to="/cadastro-usuario"
                    onClick={handleNavClick("/cadastro-usuario")}
                    className="block px-3 py-2 rounded-md text-sm text-slate-700 "
                  >
                    Cadastrar Usuário
                  </Link>
                  <Link
                    to="/vendedores"
                    onClick={handleNavClick("/vendedores")}
                    className="block px-3 py-2 rounded-md text-sm text-slate-700 "
                  >
                    Vendedores
                  </Link>
                  <Link
                    to="/configuracao-empresa"
                    onClick={handleNavClick("/configuracao-empresa")}
                    className="block px-3 py-2 rounded-md text-sm text-slate-700 "
                  >
                    Configuração da Empresa
                  </Link>
                  {(can(usuario, "cadastroUsuario") || can(usuario, "custos")) && (
                    <Link
                      to="/custos"
                      onClick={handleNavClick("/custos")}
                      className="block px-3 py-2 rounded-md text-sm text-slate-700 "
                    >
                      Custos
                    </Link>
                  )}
                  {can(usuario, "planilhasAuxiliares") && (
                    <Link
                      to="/planilhas-auxiliares"
                      onClick={handleNavClick("/planilhas-auxiliares")}
                      className="block px-3 py-2 rounded-md text-sm text-slate-700 "
                    >
                      Planilhas Auxiliares
                    </Link>
                  )}
                  {temAcessoSaude && (
                    <Link
                      to="/saude-sistema"
                      onClick={handleNavClick("/saude-sistema")}
                      className="block px-3 py-2 rounded-md text-sm text-slate-700 "
                    >
                      Saúde do Sistema
                    </Link>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
